import { useCallback, useEffect, useState } from 'react';
import type { BackendFunctionRef } from './backend';

let invalidationVersion = 0;
const invalidationListeners = new Set<() => void>();

/** Refetch every mounted query (after a change here, or when the server says data changed). */
export function invalidateQueries() {
  invalidationVersion += 1;
  for (const listener of invalidationListeners) listener();
}

async function rpc<T>(reference: BackendFunctionRef, args: unknown): Promise<T> {
  const response = await fetch(`/api/rpc/${reference.path.replace(':', '/')}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(args ?? {}),
  });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(body?.error || `Backend request failed (${response.status})`);
  }
  return body?.data as T;
}

export function useQuery<T = any>(
  reference: BackendFunctionRef,
  args: Record<string, unknown> | 'skip' = {}
): T | undefined {
  const serializedArgs = args === 'skip' ? 'skip' : JSON.stringify(args);
  const [value, setValue] = useState<T | undefined>(undefined);
  const [version, setVersion] = useState(invalidationVersion);

  useEffect(() => {
    const listener = () => setVersion(invalidationVersion);
    invalidationListeners.add(listener);
    return () => {
      invalidationListeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    if (args === 'skip') {
      setValue(undefined);
      return;
    }
    let cancelled = false;
    void rpc<T>(reference, JSON.parse(serializedArgs)).then((result) => {
      if (!cancelled) setValue(result);
    }).catch((error) => {
      if (!cancelled) console.error(`Query ${reference.path} failed:`, error);
    });
    return () => {
      cancelled = true;
    };
  }, [reference, serializedArgs, version]);

  return value;
}

function useCallable(reference: BackendFunctionRef) {
  return useCallback(
    async (args: Record<string, any> = {}): Promise<any> => {
      const result = await rpc<any>(reference, args);
      invalidateQueries();
      return result;
    },
    [reference]
  );
}

export function useMutation(reference: BackendFunctionRef) {
  return useCallable(reference);
}

export function useAction(reference: BackendFunctionRef) {
  return useCallable(reference);
}

export async function callBackend<T = any>(
  reference: BackendFunctionRef,
  args: Record<string, unknown> = {}
) {
  return rpc<T>(reference, args);
}

/** Like useQuery, but also reports the error (e.g. a CRM feature that is switched off). */
export function useQueryState<T = any>(
  reference: BackendFunctionRef,
  args: Record<string, unknown> | 'skip' = {}
): { data: T | undefined; error: string | null; isLoading: boolean } {
  const serializedArgs = args === 'skip' ? 'skip' : JSON.stringify(args);
  const [state, setState] = useState<{ data: T | undefined; error: string | null; key: string }>({
    data: undefined,
    error: null,
    key: serializedArgs,
  });
  const [version, setVersion] = useState(invalidationVersion);

  useEffect(() => {
    const listener = () => setVersion(invalidationVersion);
    invalidationListeners.add(listener);
    return () => {
      invalidationListeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    if (args === 'skip') {
      setState({ data: undefined, error: null, key: serializedArgs });
      return;
    }
    let cancelled = false;
    void rpc<T>(reference, JSON.parse(serializedArgs))
      .then((data) => {
        if (!cancelled) setState({ data, error: null, key: serializedArgs });
      })
      .catch((error) => {
        if (!cancelled) setState((previous) => ({ ...previous, error: error instanceof Error ? error.message : String(error), key: serializedArgs }));
      });
    return () => {
      cancelled = true;
    };
  }, [reference, serializedArgs, version]);

  const current = state.key === serializedArgs;
  return {
    data: current ? state.data : undefined,
    error: current ? state.error : null,
    isLoading: args !== 'skip' && (!current || (state.data === undefined && state.error === null)),
  };
}

type PageResult<T> = { page: T[]; isDone: boolean; continueCursor: string };

/**
 * Server-paged lists with "Load more". Refreshes replay the pages already requested.
 */
export function usePaginatedQuery<T = any>(
  reference: BackendFunctionRef,
  args: Record<string, unknown> | 'skip',
  pageSize = 50
) {
  const serializedArgs = args === 'skip' ? 'skip' : JSON.stringify(args);
  const [pageCount, setPageCount] = useState(1);
  const [state, setState] = useState<{ items: T[]; isDone: boolean; error: string | null; key: string } | null>(null);
  const [version, setVersion] = useState(invalidationVersion);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    const listener = () => setVersion(invalidationVersion);
    invalidationListeners.add(listener);
    return () => {
      invalidationListeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    setPageCount(1);
  }, [serializedArgs, pageSize]);

  useEffect(() => {
    if (args === 'skip') {
      setState(null);
      return;
    }
    let cancelled = false;
    const base = JSON.parse(serializedArgs);
    const fetchPages = async () => {
      const items: T[] = [];
      let cursor: string | null = null;
      let isDone = false;
      for (let page = 0; page < pageCount && !isDone; page += 1) {
        const result: PageResult<T> = await rpc<PageResult<T>>(reference, { ...base, paginationOpts: { numItems: pageSize, cursor } });
        items.push(...result.page);
        isDone = result.isDone;
        if (!isDone && (!result.continueCursor || result.continueCursor === cursor)) throw new Error('Unable to advance the list');
        cursor = result.continueCursor;
      }
      return { page: items, isDone };
    };
    void fetchPages()
      .then((result) => {
        if (!cancelled) setState({ items: result.page, isDone: result.isDone, error: null, key: serializedArgs });
      })
      .catch((error) => {
        if (!cancelled) setState({ items: [], isDone: true, error: error instanceof Error ? error.message : String(error), key: serializedArgs });
      })
      .finally(() => {
        if (!cancelled) setLoadingMore(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reference, serializedArgs, version, pageCount, pageSize]);

  const current = state?.key === serializedArgs ? state : null;
  return {
    items: current?.items ?? [],
    isLoading: args !== 'skip' && !current,
    isDone: current?.isDone ?? false,
    error: current?.error ?? null,
    loadingMore,
    loadMore: () => {
      setLoadingMore(true);
      setPageCount((count) => count + 1);
    },
  };
}
